import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import {
  ChatCircleDots, X, PaperPlaneTilt, User as UserIcon, Sparkle,
  CheckCircle, WarningCircle, Lifebuoy,
} from "@phosphor-icons/react";

const STORAGE_KEY = "mise_support_chat_v1";
const GREETING = {
  role: "assistant",
  content:
    "¡Hola! Soy Pinche, tu asistente. Pregúntame cómo usar MIZETA, dudas de cocina, sustituciones de ingredientes o conversión de medidas. Si tengo un día malo o no puedo ayudarte, toca \"Hablar con humano\" abajo y le pasamos tu caso al equipo.",
};

const QUICK_PROMPTS = [
  "¿Cómo extraigo una receta de un Reel de Instagram?",
  "¿Cuánto pesa una taza de harina?",
  "YouTube no encuentra suficiente información para mi receta",
  "¿Cómo cancelo mi suscripción Pro?",
];

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) return JSON.parse(cached);
    } catch {}
    return [GREETING];
  });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateMsg, setEscalateMsg] = useState("");
  const [escalateSent, setEscalateSent] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20))); } catch {}
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [open, messages, escalateOpen]);

  const send = useCallback(async (text, contextText, importContext) => {
    const content = (text ?? draft).trim();
    if (!content || sending) return;
    setError("");
    const next = [...messages, { role: "user", content }];
    const requestMessages = contextText
      ? [...messages, { role: "user", content: contextText }]
      : next;
    setMessages(next);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", {
        messages: requestMessages.slice(-12),
        import_context: importContext || null,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setError(err.response?.data?.detail || "El asistente no está disponible. Inténtalo de nuevo en un momento.");
    } finally {
      setSending(false);
    }
  }, [draft, messages, sending]);

  useEffect(() => {
    const openImportHelp = (event) => {
      const detail = event.detail || {};
      setOpen(true);
      window.setTimeout(() => send(
        detail.displayMessage || "Necesito ayuda para completar esta receta de YouTube.",
        detail.contextMessage,
        detail.importContext,
      ), 0);
    };
    window.addEventListener("mizeta:pinche-youtube-help", openImportHelp);
    window.addEventListener("mizeta:pinche-instagram-help", openImportHelp);
    return () => {
      window.removeEventListener("mizeta:pinche-youtube-help", openImportHelp);
      window.removeEventListener("mizeta:pinche-instagram-help", openImportHelp);
    };
  }, [send]);

  const escalate = async () => {
    if (escalateMsg.trim().length < 5) return;
    setEscalating(true);
    try {
      await api.post("/support/ticket", {
        message: escalateMsg.trim(),
        recent_messages: messages.slice(-10),
      });
      setEscalateSent(true);
      setEscalateMsg("");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar el mensaje.");
    } finally {
      setEscalating(false);
    }
  };

  const reset = () => {
    setMessages([GREETING]);
    setEscalateSent(false);
    setEscalateOpen(false);
    setError("");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Ayuda"
        data-testid="support-fab"
        className="fixed bottom-20 right-5 sm:bottom-24 sm:right-6 z-40 w-14 h-14 rounded-full bg-[hsl(var(--primary))] text-white shadow-xl hover:shadow-2xl btn-press flex items-center justify-center"
      >
        <ChatCircleDots size={26} weight="fill" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end pb-20 sm:items-center sm:justify-end sm:p-6 sm:pb-20 bg-[hsl(var(--foreground))]/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="support-panel"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md h-[85vh] sm:h-[640px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden fade-up"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center">
                  <Sparkle size={18} weight="fill" className="text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <p className="font-serif text-lg leading-tight">Pinche</p>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-widest">Asistente de MIZETA</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="support-close" className="text-[hsl(var(--muted-foreground))] btn-press">
                <X size={22} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}-${i}`}>
                  {m.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkle size={14} weight="fill" className="text-[hsl(var(--primary))]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[hsl(var(--foreground))] text-white rounded-tr-sm"
                        : "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] rounded-tl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                  {m.role === "user" && (
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--foreground))] flex items-center justify-center shrink-0 mt-0.5">
                      <UserIcon size={14} weight="fill" className="text-white" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-2" data-testid="support-typing">
                  <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center shrink-0">
                    <Sparkle size={14} weight="fill" className="text-[hsl(var(--primary))]" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl bg-[hsl(var(--muted))] flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground))] simmer" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground))] simmer-2" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--muted-foreground))] simmer-3" />
                  </div>
                </div>
              )}
              {/* Quick prompts shown only on first turn */}
              {messages.length <= 1 && !sending && (
                <div className="pt-2 flex flex-col gap-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`quick-${q.slice(0, 10)}`}
                      className="text-left text-sm px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] btn-press"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {error && (
                <div className="text-xs text-[hsl(var(--destructive))] flex gap-1.5 items-center">
                  <WarningCircle size={14} /> {error}
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Escalation pane */}
            {escalateOpen && (
              <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 p-4" data-testid="escalate-pane">
                {escalateSent ? (
                  <div className="text-center py-3">
                    <CheckCircle size={32} weight="duotone" className="mx-auto text-[hsl(var(--secondary))]" />
                    <p className="mt-2 font-serif text-lg">¡Recibido!</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      Te responderemos al email de tu cuenta lo antes posible.
                    </p>
                    <button onClick={reset} data-testid="escalate-done" className="mt-3 text-sm font-medium text-[hsl(var(--primary))]">
                      Cerrar
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="eyebrow mb-2 text-[10px]">explica tu problema al equipo</p>
                    <textarea
                      data-testid="escalate-input"
                      value={escalateMsg}
                      onChange={(e) => setEscalateMsg(e.target.value)}
                      placeholder="Cuéntanos qué ocurre. Lo antes posible te respondemos al email de tu cuenta."
                      rows={3}
                      maxLength={4000}
                      className="w-full bg-white border border-[hsl(var(--border))] rounded-xl p-3 text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
                    />
                    <div className="mt-2 flex gap-2 items-center justify-between">
                      <button onClick={() => setEscalateOpen(false)} className="text-xs text-[hsl(var(--muted-foreground))]">
                        Volver al chat
                      </button>
                      <button
                        onClick={escalate}
                        disabled={escalating || escalateMsg.trim().length < 5}
                        data-testid="escalate-send"
                        className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold btn-press disabled:opacity-50"
                      >
                        {escalating ? "Enviando…" : "Enviar al equipo"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Input */}
            {!escalateOpen && (
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-[hsl(var(--border))] p-3 flex items-center gap-2"
              >
                <input
                  data-testid="support-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Escribe a Pinche…"
                  disabled={sending}
                  className="flex-1 px-4 py-2.5 bg-[hsl(var(--background))] rounded-full border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  data-testid="support-send"
                  className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center btn-press disabled:opacity-40"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </form>
            )}

            {/* Bottom action */}
            {!escalateOpen && (
              <div className="px-3 pb-3 -mt-1">
                <button
                  onClick={() => setEscalateOpen(true)}
                  data-testid="escalate-open"
                  className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] py-1"
                >
                  <Lifebuoy size={14} /> Hablar con humano
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
